import React from 'react';
import { useTranslation } from 'react-i18next';
import { useScrollTop } from 'Core/hooks/useScrollTop';
import Page from 'Core/components/Page';
import Card from 'Core/components/FormElements/Card';
import Row from 'Core/components/Layout/Row';
import Column from 'Core/components/Layout/Column';
import warningIllustration from 'url:Images/illustrations/warning.svg';
import { createSecondaryFinancingPath } from 'Modules/fortiva/routes';
import InfoBox from 'Core/components/InfoBox';

import { PrimaryButton } from 'Core/components/Button';
import { useNavigate } from 'react-location';
import classNames from 'classnames';

// Toggle to show/hide the rejection reason on the declined page.
const SHOW_REJECTION_REASON = false;

export default function FinancingDeclined({
  allowCreateApplication = false,
  application = {},
  isRejected = false,
}) {
  document.title = 'Payment Portal - Application Declined';
  const { t } = useTranslation();
  useScrollTop();

  const navigate = useNavigate();
  const { customer_id = '', payload: { relationship = '' } = {} } = application;
  const message = application?.payload?.correction_required?.message || '';
  const reasonMessage = message.replace(/\{[^}]*\}/g, '').trim();
  const handleCreateApplication = () => {
    const relationshipObj = JSON.stringify({ relationship, customer_id });
    sessionStorage.setItem('relationship', relationshipObj);
    navigate({
      to: createSecondaryFinancingPath,
      search: true,
    });
  };
  return (
    <Page
      title={t('page.financing.header')}
      withStepProgress={false}
      stickyFooter
      branding="fortiva"
    >
      <Card className="justify-center !p-6 !pb-6 mt-8 mx-48" cardPadding="sm">
        <header className="relative pb-4 mb-4 border-b border-slate-200">
          <span className="block text-[0.7rem] font-bold tracking-[0.18em] uppercase text-amber-600">
            Fortiva Retail Credit
          </span>
          <h1
            className="!mt-1 !mb-0 text-2xl md:text-3xl font-semibold tracking-tight text-slate-800"
            data-testid="title-finance-declined"
          >
            {isRejected
              ? 'Financing Application Rejected'
              : t('common.nextgen.financingDeclined.title')}
          </h1>
          <span
            aria-hidden="true"
            className="absolute left-0 -bottom-px h-[2px] w-28 rounded bg-gradient-to-r from-amber-500 via-amber-600 to-transparent"
          />
        </header>
        <Row className="items-start justify-center">
          <Column className="!flex-col-3 flex justify-center mt-0 !mb-0">
            <img alt="application-declined" width="200em" src={warningIllustration}></img>
          </Column>
          <Column className="!flex-col-9 !mb-0">
            <div
              className={classNames(
                'mb-0 w-full whitespace-pre-line text-lg leading-loose font-light mt-4'
              )}
            >
              Fortiva is unable to prequalify you at this time. You will receive written information
              related to this request in 7-10 days.
            </div>
            {SHOW_REJECTION_REASON && isRejected && reasonMessage && (
              <div className="mt-4">
                <InfoBox type="warningLite" showBorder>
                  <div className="font-bold mr-2 text-yellow-700 mb-0 inline-block">Reason:</div>{' '}
                  {reasonMessage}
                </InfoBox>
              </div>
            )}
            {allowCreateApplication && (
              <PrimaryButton
                data-testid="btn-create-secondary-application"
                className="mt-6 px-12"
                onClick={handleCreateApplication}
              >
                {t('common.reapply')}
              </PrimaryButton>
            )}
          </Column>
        </Row>
      </Card>
    </Page>
  );
}
